import { Component, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { SampleGenerationType, SampleType } from "@app/controls/testing/advanced/shared/enums";
import {
    EffectivenessModel,
    EffectivenessSourceDocumentsModel,
    WorkflowGenerateSamplesModel,
} from "@app/controls/testing/advanced/shared/models";
import { GenerateSamplesStep } from "./models/generate-samples-steps.enum";
import { ManualSamplesGroup } from "./steps/manual-samples-step/models/manual-sample.model";
import { SampleTypeFormGroup } from "./steps/sample-type-step/models/sample-type-form-group.model";
import { SampleTypeStepComponent } from "./steps/sample-type-step/sample-type-step.component";
import { ManualSamplesStepComponent } from "./steps/manual-samples-step/manual-samples-step.component";
import { EffPopupHeaderComponent } from "../../../common/popups/eff-popup-header/eff-popup-header.component";
import { EffButtonComponent } from "../../../common/eff-button/eff-button.component";

@Component({
    selector: "generate-samples-popup",
    templateUrl: "./generate-samples-popup.component.html",
    styleUrls: ["./generate-samples-popup.component.scss"],
    imports: [EffPopupHeaderComponent, SampleTypeStepComponent, ManualSamplesStepComponent, EffButtonComponent]
})
export class GenerateSamplesPopupComponent {
    data = inject(MAT_DIALOG_DATA);
    dialogRef = inject<MatDialogRef<GenerateSamplesPopupComponent>>(MatDialogRef);

    samplingInformation: WorkflowGenerateSamplesModel;
    effectivenessModel: EffectivenessModel;
    sourceDocumentModel: EffectivenessSourceDocumentsModel;
    availableSampleTypes: any[];
    currentStep: GenerateSamplesStep = GenerateSamplesStep.SampleType;
    isAutomatic: boolean = true;
    title: string;

    manualSamplesGroup: ManualSamplesGroup = new ManualSamplesGroup();
    sampleTypeFormGroup: SampleTypeFormGroup = new SampleTypeFormGroup();

    backButtonName: string;
    nextButtonName: string;

    private isSaving: boolean;

    constructor() {
        this.createDefaultSamplingInformationModel();
        this.effectivenessModel = this.data.effectivenessModel;
        this.sourceDocumentModel = this.data.sourceDocumentModel;
        this.availableSampleTypes = this.data.availableSampleTypes;
        this.isAutomatic =
            this.effectivenessModel.sampleGenerationType == SampleGenerationType.automatic;
        this.title = this.isAutomatic ? "Automatic Sampling" : "Manual Sampling";
        this.initStep();
    }

    private createDefaultSamplingInformationModel() {
        this.samplingInformation = new WorkflowGenerateSamplesModel();
        this.samplingInformation.sampleType = SampleType.number;
        this.samplingInformation.populationSize = this.data.populationSize;
        this.samplingInformation.numberOfTestSamples = 1;
    }

    async onNextStep() {
        if (!this.sampleTypeFormGroup.checkAndDisplayValidity()) {
            return;
        }

        this.isSaving = true;

        try {
            if (this.isAutomatic) {
                this.sampleTypeFormGroup.updateSample(
                    this.samplingInformation,
                    this.sourceDocumentModel,
                );
                await this.save();
            } else {
                if (this.currentStep == GenerateSamplesStep.SampleType) {
                    this.sampleTypeFormGroup.updateSample(
                        this.samplingInformation,
                        this.sourceDocumentModel,
                    );
                    this.currentStep = GenerateSamplesStep.ManualSamples;
                } else {
                    this.manualSamplesGroup.updateSample(this.samplingInformation);
                    await this.save();
                }
            }
        } finally {
            this.initStep();
            this.isSaving = false;
        }
    }

    onPreviousStep() {
        if (this.isAutomatic) {
            this.dialogRef.close();
        } else {
            if (this.currentStep == GenerateSamplesStep.SampleType) {
                this.dialogRef.close();
            } else {
                this.currentStep = GenerateSamplesStep.SampleType;
            }
        }
        this.initStep();
    }

    initStep() {
        if (this.isAutomatic) {
            this.backButtonName = "Cancel";
            this.nextButtonName = "Generate Samples";
        } else {
            if (this.currentStep == GenerateSamplesStep.SampleType) {
                this.backButtonName = "Previous step";
                this.nextButtonName = "Specify samples";
            }

            if (this.currentStep == GenerateSamplesStep.ManualSamples) {
                this.backButtonName = "Previous step";
                this.nextButtonName = "Generate Samples";
            }
        }
    }

    get isNextButtonEnabled() {
        if (this.isSaving) {
            return false;
        }
        if (this.currentStep == GenerateSamplesStep.SampleType) {
            return this.sampleTypeFormGroup.isAllFilled();
        } else {
            return this.manualSamplesGroup.isAllFilled();
        }
    }

    async save() {
        if (this.data.save) {
            await this.data.save(this.samplingInformation);
        }
    }
}
